import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs/internal/Observable';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import { Subject } from 'rxjs/internal/Subject';
import { map } from 'rxjs/operators';
import { BsModalConfirmComponent, createConfirmModalOptions } from '../core/modal/modal-confirm.component';
import { AdaptiveRule } from './state/adaptive-rule.model';
import { AdaptiveRulesService } from './state/adaptive-rules.service';
import { BsModalAlertComponent, createAlertModalOptions } from '../core/modal/modal-alert.component';
import { Subscription } from 'rxjs/internal/Subscription';

@Component({
    selector: 'app-adaptive-rules',
    templateUrl: './adaptive-rules.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdaptiveRulesComponent implements OnInit {
    onClose: Subject<boolean>;
    viewModel$: Observable<any>;
    loading$: Observable<boolean>;
    updatedRule: AdaptiveRule;
    isUpdated = false;
    isNewRule = false;
    userRules$: Observable<Array<AdaptiveRule>>;
    selectedUserId: any = undefined;
    categoryType: string = undefined;
    selectedRule: AdaptiveRule = undefined;
    deleteLoading: boolean = false;
    subscription = new Subscription();

    constructor(public adaptiveRuleBsModalRef: BsModalRef,
                private adaptiveRulesService: AdaptiveRulesService,
                private modalService: BsModalService) {
        this.loading$ = this.adaptiveRulesService.loading$;
        this.userRules$ = this.adaptiveRulesService.entities$;
    }

    ngOnInit(): void {
        this.onClose = new Subject();
        const config: any = {...this.modalService.config.initialState};
        if (config.userId !== undefined && config.userId !== 0) {
            this.selectedUserId = config.userId;
            this.adaptiveRulesService.clearCache();
            this.adaptiveRulesService.getWithQuery({userId: this.selectedUserId});
            this.categoryType = config.categoryType;
        } else {
            this.adaptiveRulesService.getAll();
        }
        this.viewModel$ = combineLatest([
            this.loading$,
            this.userRules$]
        )
            .pipe(
                map(([loading, userRules]) =>
                    ({
                        loading, userRules
                    }))
            );
    }

    public onCancel(): void {
       if (this.isUpdated) {
            const modalRef = this.modalService
                .show(BsModalConfirmComponent, createConfirmModalOptions('Confirm',
                    'You will loose all unsaved changes. Are you sure you want to exit?',
                    'Yes', 'No'));

            modalRef.content.onClose
                .subscribe(dialogResult => {
                    if (dialogResult.isConfirmed) {
                        this.isUpdated = false;
                        this.adaptiveRuleBsModalRef.hide();
                        this.onClose.next(dialogResult);
                    }
                });
       } else {
            this.onClose.next(false);
            this.adaptiveRuleBsModalRef.hide();
       }
    }

    saveRule(): void {
        this.isUpdated = false;

        if (this.selectedUserId !== undefined) {
            this.updatedRule.userId = this.selectedUserId;
        }
        if (this.isNewRule) {
            if (this.updatedRule.mediaType === 'voice' && this.updatedRule.ruleName.indexOf('voice') === -1)
            {
                const ruleName = this.updatedRule.ruleName.startsWith('voice_') ? this.updatedRule.ruleName : 'voice_' + this.updatedRule.ruleName;
                this.updatedRule.ruleName = ruleName;
            }
            if (this.updatedRule.mediaType === 'twiliovoice')
            {
                const ruleName = this.updatedRule.ruleName.startsWith('twiliovoice_') ? this.updatedRule.ruleName : 'twiliovoice_' + this.updatedRule.ruleName;
                this.updatedRule.ruleName = ruleName;
            }
            this.adaptiveRulesService.add(this.updatedRule);
        } else {
            this.adaptiveRulesService.update(this.updatedRule);
        }

        this.onClose.next(false);
        this.adaptiveRuleBsModalRef.hide();
    }

    updateRule(event: AdaptiveRule, userRules): void {
        this.isUpdated = false;
        if (event !== undefined && event.type === "map") {
            this.updatedRule = event;
            if (userRules.length === 0 && this.isNewRule) {
                this.isUpdated = true;
            }
            userRules.map(userRule => {
                if (this.isNewRule) {
                    this.isUpdated = this.updatedRule.ruleName !== undefined &&
                        this.updatedRule.ruleName !== null && this.updatedRule.ruleName !== '';
                } else {
                    if (this.updatedRule.ruleName === userRule.ruleName) {
                        let isMapUpdated = false; // if value in map array is updated
                        this.updatedRule.map.findIndex(rule => {
                            const key = Object.keys(rule)[0];
                            const value = Object.values(rule)[0];
                            userRule.map.find(userrule => {
                                const userrulekey = Object.keys(userrule)[0];
                                const userrulevalue = Object.values(userrule)[0];
                                if (userrulekey === key) {
                                    isMapUpdated = userrulevalue !== value;
                                }
                            })
                        });
                        this.isUpdated = this.updatedRule.mediaType !== userRule.mediaType ||
                            this.updatedRule.default !== userRule.default || isMapUpdated ||
                            this.updatedRule.map.length !== userRule.map.length;
                    }
                }

                return userRule;
            });
        }
    }

    isNew(event): void {
        this.isNewRule = event;
    }

    selectRule(event: AdaptiveRule, selectedUserId): void {
        this.deleteLoading = false;
        if (event === undefined ) {
            this.selectedRule = event;
        } else if (event.type === "map") {
            let selectedRule = {...event};
            selectedRule.userId = selectedUserId;
            this.selectedRule = selectedRule;
        }
    }

    deleteRule(selectedUserId): void {
        this.deleteLoading = true;
        if (!this.isNewRule) {
            this.subscription.add(
                this.adaptiveRulesService.canDeleteRule(selectedUserId, this.selectedRule.ruleName).subscribe(response => {
                    if (response.canDelete) {
                        this.deleteSelectedRule(selectedUserId);
                    } else {
                        const templateNames = response.templateNames;
                        this.deleteLoading = false;
                        this.modalService
                            .show(BsModalAlertComponent, createAlertModalOptions('Alert',
                                'The Adaptive rule is being used in ' + templateNames +'. Please remove it first?'));
                    }
                }));
        }
    }

    deleteSelectedRule(selectedUserId): void {
        this.subscription.add(this.adaptiveRulesService.deleteRule(selectedUserId, this.selectedRule.ruleName).subscribe(_ => {
                this.adaptiveRulesService.clearCache();
                this.adaptiveRulesService.getWithQuery({userId: selectedUserId});
                this.isUpdated = false;
            })
        );
    }
}
